import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { ArrowUpRight, Mail, MapPin, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  region: "",
  property_type: "residential",
  service_interest: "full_installation",
  system_size: "",
  timeline: "",
  preferred_contact: "email",
  message: "",
};

const regions = [
  "New Plymouth",
  "Bell Block",
  "Waitara",
  "Inglewood",
  "Ōakura",
  "Stratford",
  "Hāwera",
  "Other (Taranaki)",
  "Outside Taranaki — please ask",
];

const services = [
  { v: "full_installation", l: "Full solar installation" },
  { v: "grid_connection", l: "Grid connection" },
  { v: "battery", l: "Battery install" },
  { v: "micro_inverter", l: "Micro inverters" },
  { v: "inverter", l: "Inverter (string)" },
];

const sizes = ["Not sure yet", "Under 3 kW", "3–5 kW", "5–8 kW", "8–12 kW", "12+ kW"];
const timelines = [
  { v: "asap", l: "ASAP" },
  { v: "1-3_months", l: "Within 1–3 months" },
  { v: "3-6_months", l: "3–6 months" },
  { v: "exploring", l: "Just exploring" },
];

const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.region) {
      toast.error("Please fill in your name, email, phone and region.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      setDone(data);
      toast.success("Quote request received — we’ll be in touch within 24 hours.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-32 bg-[#1A3626] text-white overflow-hidden">
      <div className="absolute inset-0 -z-0 opacity-20">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F5C242] blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-[#234B35] blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#F5C242]">
              · Request a quote
            </div>
            <h2
              data-testid="quote-heading"
              className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 leading-[1.05]"
            >
              Tell us about your roof. We&rsquo;ll do the rest.
            </h2>
            <p className="mt-6 text-base text-white/75 max-w-md leading-relaxed">
              Free, no-obligation quotes. Share a few
              details and one of our NZ-based specialists will be in touch.
            </p>

            <div className="mt-10 space-y-4 text-sm text-white/80">
              <div className="flex items-center gap-3" data-testid="contact-email">
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-[#F5C242]" />
                </span>
                admin@centralsolar.co.nz
              </div>
              <div className="flex items-center gap-3" data-testid="contact-area">
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-[#F5C242]" />
                </span>
                Serving New Plymouth & surrounding areas
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="quote-success"
                className="rounded-3xl bg-white text-[#1A3626] p-10 border border-[#F5C242]"
                style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.25)" }}
              >
                <div className="h-12 w-12 rounded-full bg-[#F5C242]/20 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#1A3626]" />
                </div>
                <h3 className="font-display text-3xl mt-6">Kia ora, {done.full_name.split(' ')[0]}!</h3>
                <p className="mt-3 text-[#5C6B61]">
                  We&rsquo;ve received your request and emailed our specialist team.
                  Expect a reply within 24 business hours.
                </p>
                <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[#5C6B61]">
                  Reference: <span data-testid="quote-success-id" className="text-[#1A3626] font-medium tracking-wider normal-case">{done.id.slice(0, 8)}</span>
                </div>
                <Button
                  data-testid="quote-success-new"
                  onClick={() => setDone(null)}
                  className="mt-8 rounded-full bg-[#1A3626] text-white hover:bg-[#234B35] h-12 px-6"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form
                data-testid="quote-form"
                onSubmit={submit}
                className="rounded-3xl bg-white text-[#1A3626] p-8 lg:p-10 border border-white/20"
                style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.25)" }}
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="full_name">Full name *</Label>
                    <Input
                      data-testid="quote-input-name"
                      id="full_name"
                      value={form.full_name}
                      onChange={(e) => set("full_name", e.target.value)}
                      placeholder="Jane Ngata"
                      className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40 focus-visible:ring-[#1A3626]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      data-testid="quote-input-email"
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="jane@example.co.nz"
                      className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40 focus-visible:ring-[#1A3626]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      data-testid="quote-input-phone"
                      id="phone"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="021 555 0100"
                      className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40 focus-visible:ring-[#1A3626]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Region *</Label>
                    <Select value={form.region} onValueChange={(v) => set("region", v)}>
                      <SelectTrigger data-testid="quote-select-region" className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40">
                        <SelectValue placeholder="Choose your region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => (
                          <SelectItem key={r} value={r} data-testid={`region-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Property type</Label>
                    <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                      <SelectTrigger data-testid="quote-select-property" className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="rural">Rural / farm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>I&rsquo;m interested in</Label>
                    <Select value={form.service_interest} onValueChange={(v) => set("service_interest", v)}>
                      <SelectTrigger data-testid="quote-select-service" className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>System size</Label>
                    <Select value={form.system_size} onValueChange={(v) => set("system_size", v)}>
                      <SelectTrigger data-testid="quote-select-size" className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40">
                        <SelectValue placeholder="Pick a size" />
                      </SelectTrigger>
                      <SelectContent>
                        {sizes.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label>Timeline</Label>
                    <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                      <SelectTrigger data-testid="quote-select-timeline" className="h-12 rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40">
                        <SelectValue placeholder="When would you like to start?" />
                      </SelectTrigger>
                      <SelectContent>
                        {timelines.map((t) => (
                          <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3 sm:col-span-2">
                    <Label>Preferred contact</Label>
                    <RadioGroup
                      value={form.preferred_contact}
                      onValueChange={(v) => set("preferred_contact", v)}
                      className="flex flex-wrap gap-3"
                    >
                      {[
                        { v: "email", l: "Email" },
                        { v: "phone", l: "Phone" },
                      ].map((o) => (
                        <label
                          key={o.v}
                          data-testid={`quote-contact-${o.v}`}
                          className={`flex items-center gap-2 rounded-full border px-4 py-2.5 cursor-pointer transition-colors ${
                            form.preferred_contact === o.v
                              ? "border-[#1A3626] bg-[#1A3626] text-white"
                              : "border-[#D2D7D0] hover:border-[#1A3626]"
                          }`}
                        >
                          <RadioGroupItem value={o.v} className={form.preferred_contact === o.v ? "border-white text-white" : ""} />
                          <span className="text-sm">{o.l}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="message">Anything else we should know?</Label>
                    <Textarea
                      data-testid="quote-input-message"
                      id="message"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Roof orientation, current power bill, special requirements..."
                      className="min-h-[120px] rounded-xl border-[#D2D7D0] bg-[#F7F6F2]/40 focus-visible:ring-[#1A3626]"
                    />
                  </div>
                </div>

                <Button
                  data-testid="quote-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="mt-8 w-full sm:w-auto rounded-full bg-[#1A3626] text-white hover:bg-[#234B35] h-14 px-8 text-base group"
                >
                  {submitting ? "Sending…" : "Send quote request"}
                  {!submitting && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
                </Button>

                <p className="mt-4 text-xs text-[#5C6B61]">
                  By submitting you agree to be contacted by Central Solar regarding your enquiry. We never share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
